import {
  CanActivate,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Injectable } from "@angular/core";
import { firstValueFrom, of } from "rxjs";
import { catchError } from "rxjs/operators";

import { AuthService } from "./auth.service";
import { TeamsService } from "../teams/teams.service";

/**
 * Allows league staff or the team’s captain or co-captain for `route.params.teamId`.
 */
@Injectable()
export class AuthGuardAdminOrTeamCaptain implements CanActivate {
  constructor(
    private authService: AuthService,
    private teamsService: TeamsService,
    private router: Router
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    routerState: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    let teamId: string | null = null;
    for (let s: ActivatedRouteSnapshot | null = route; s; s = s.parent) {
      const id = s.paramMap.get("teamId");
      if (id) {
        teamId = id;
        break;
      }
    }
    if (!teamId) {
      return this.router.createUrlTree(["/auth/unauthorized"]);
    }

    const authState = await firstValueFrom(this.authService.getAuthStatusListener());
    if (!authState.isAuthenticated) {
      return this.router.createUrlTree(["/auth/login"]);
    }

    if (this.authService.isAdmin()) {
      try {
        const profile = await firstValueFrom(this.authService.getProfile());
        if (!this.authService.isProfileComplete(profile)) {
          return this.router.createUrlTree(["/auth/complete-profile"], {
            queryParams: { returnUrl: routerState.url },
          });
        }
      } catch {
        return this.router.createUrlTree(["/auth/unauthorized"]);
      }
      return true;
    }

    try {
      const [profile, team] = await Promise.all([
        firstValueFrom(this.authService.getProfile()),
        firstValueFrom(
          this.teamsService.getTeam(teamId).pipe(catchError(() => of(null)))
        ),
      ]);

      if (!team || !profile?.id) {
        return this.router.createUrlTree(["/auth/unauthorized"]);
      }

      const uid = String(profile.id);
      const captainId =
        team.captainId != null ? String(team.captainId) : null;
      const coCaptainId =
        team.coCaptainId != null ? String(team.coCaptainId) : null;

      if (captainId === uid || coCaptainId === uid) {
        if (!this.authService.isProfileComplete(profile)) {
          return this.router.createUrlTree(["/auth/complete-profile"], {
            queryParams: { returnUrl: routerState.url },
          });
        }
        return true;
      }

      return this.router.createUrlTree(["/auth/unauthorized"]);
    } catch {
      return this.router.createUrlTree(["/auth/unauthorized"]);
    }
  }
}
